<template>
  <section ref="sectionRef" class="pilares">
    <div class="pilares__transition-in"></div>
    <div ref="atmosphereRef" class="pilares__atmosphere"></div>
    <div class="pilares__volumetric"></div>

    <div class="pilares__inner">
      <div ref="headerRef" class="pilares__header">
        <h2 class="pilares__title">Nossos Pilares de Atuação</h2>
        <p class="pilares__subtitle">
          Da estratégia à execução, cobrimos todas as pontas da sua jornada
          digital.
        </p>
      </div>

      <div ref="gridRef" class="pilares__grid">
        <article
          v-for="(pilar, index) in pilares"
          :key="index"
          ref="cardRefs"
          class="pilar-card"
          @mousemove="handleCardMouse($event, index)"
          @mouseleave="handleCardLeave(index)"
        >
          <div
            class="pilar-card__texture"
            :style="{ backgroundImage: `url(${pilar.texture})` }"
          ></div>
          <div class="pilar-card__glow" :style="cardGlowStyles[index]"></div>
          <div class="pilar-card__shine" :style="cardShineStyles[index]"></div>
          <div class="pilar-card__depth">
            <div class="pilar-card__icon" v-html="pilar.icon"></div>
            <h3 class="pilar-card__title">{{ pilar.title }}</h3>
          </div>
          <p class="pilar-card__desc">{{ pilar.desc }}</p>
          <div class="pilar-card__index">0{{ index + 1 }}</div>
        </article>
      </div>
    </div>

    <div class="pilares__transition-out"></div>
  </section>
</template>

<script setup>
import { ref, reactive, onMounted, onUnmounted } from "vue";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const sectionRef = ref(null);
const headerRef = ref(null);
const gridRef = ref(null);
const atmosphereRef = ref(null);
const cardRefs = ref([]);

const cardGlowStyles = reactive([
  { opacity: 0 },
  { opacity: 0 },
  { opacity: 0 },
  { opacity: 0 },
]);

const cardShineStyles = reactive([
  { opacity: 0 },
  { opacity: 0 },
  { opacity: 0 },
  { opacity: 0 },
]);

function handleCardMouse(e, index) {
  const card = cardRefs.value[index];
  if (!card) return;

  const rect = card.getBoundingClientRect();
  const x = e.clientX - rect.left;
  const y = e.clientY - rect.top;
  const centerX = rect.width / 2;
  const centerY = rect.height / 2;
  const rotateX = ((y - centerY) / centerY) * -12;
  const rotateY = ((x - centerX) / centerX) * 12;

  gsap.to(card, {
    rotateX,
    rotateY,
    duration: 0.4,
    ease: "power2.out",
    transformPerspective: 800,
  });

  cardGlowStyles[index] = {
    opacity: 1,
    background: `radial-gradient(350px circle at ${x}px ${y}px, rgba(167, 139, 250, 0.14), transparent 60%)`,
  };

  const shineX = (x / rect.width) * 100;
  const shineY = (y / rect.height) * 100;
  cardShineStyles[index] = {
    opacity: 1,
    background: `radial-gradient(200px circle at ${shineX}% ${shineY}%, rgba(255, 255, 255, 0.06), transparent 60%)`,
  };
}

function handleCardLeave(index) {
  const card = cardRefs.value[index];
  if (!card) return;

  gsap.to(card, {
    rotateX: 0,
    rotateY: 0,
    duration: 0.6,
    ease: "elastic.out(1, 0.5)",
    transformPerspective: 800,
  });

  cardGlowStyles[index] = { opacity: 0 };
  cardShineStyles[index] = { opacity: 0 };
}

const pilares = [
  {
    icon: `<svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>`,
    title: "Presença Digital de Impacto",
    desc: "Desenvolvemos sites e aplicações que não são apenas bonitos, mas que geram resultados, com foco em performance e experiência do usuário.",
    texture: "/Imgs/textures/texture-digital.png",
  },
  {
    icon: `<svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><path d="M12 1v6m0 6v6"/><path d="M1 12h6m6 0h6"/><path d="m4.22 4.22 4.24 4.24m7.08 7.08 4.24 4.24"/><path d="m19.78 4.22-4.24 4.24m-7.08 7.08-4.24 4.24"/></svg>`,
    title: "Consultoria Estratégica",
    desc: "Alinhamos tecnologia aos seus objetivos, criando um roadmap claro para a transformação digital do seu negócio.",
    texture: "/Imgs/textures/texture-strategy.png",
  },
  {
    icon: `<svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20V10"/><path d="M18 20V4"/><path d="M6 20v-4"/><path d="M2 20h20"/></svg>`,
    title: "Automação de Processos",
    desc: "Otimizamos suas operações com sistemas inteligentes, reduzindo custos e liberando sua equipe para focar no crescimento.",
    texture: "/Imgs/textures/texture-automation.png",
  },
  {
    icon: `<svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="2" width="20" height="8" rx="2"/><rect x="2" y="14" width="20" height="8" rx="2"/><circle cx="6" cy="6" r="1" fill="currentColor"/><circle cx="6" cy="18" r="1" fill="currentColor"/></svg>`,
    title: "Infraestrutura Digital Robusta",
    desc: "Garantimos que sua operação online seja segura, rápida e pronta para escalar, não importa o tamanho do seu sucesso.",
    texture: "/Imgs/textures/texture-infra.png",
  },
];

let ctx;

onMounted(() => {
  ctx = gsap.context(() => {
    gsap.fromTo(
      headerRef.value,
      { autoAlpha: 0, y: 40 },
      {
        autoAlpha: 1,
        y: 0,
        duration: 1,
        ease: "power3.out",
        scrollTrigger: {
          trigger: headerRef.value,
          start: "top 85%",
          end: "top 60%",
          toggleActions: "play none none reverse",
        },
      }
    );

    gsap.to(atmosphereRef.value, {
      y: 80,
      ease: "none",
      scrollTrigger: {
        trigger: sectionRef.value,
        start: "top bottom",
        end: "bottom top",
        scrub: 1,
      },
    });

    gsap.to(gridRef.value, {
      y: -20,
      ease: "none",
      scrollTrigger: {
        trigger: sectionRef.value,
        start: "top bottom",
        end: "bottom top",
        scrub: 1,
      },
    });

    cardRefs.value.forEach((card, i) => {
      gsap.fromTo(
        card,
        {
          autoAlpha: 0,
          y: 60,
          z: -100,
          rotationY: -15,
          transformPerspective: 1200,
        },
        {
          autoAlpha: 1,
          y: 0,
          z: 0,
          rotationY: 0,
          duration: 1,
          ease: "power3.out",
          delay: i * 0.12,
          scrollTrigger: {
            trigger: card,
            start: "top 88%",
            end: "top 55%",
            toggleActions: "play none none reverse",
          },
        }
      );
    });
  }, sectionRef.value);
});

onUnmounted(() => {
  if (ctx) ctx.revert();
});
</script>

<style scoped>
.pilares {
  position: relative;
  min-height: 100vh;
  padding: 8rem 2rem;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pilares__transition-in {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 200px;
  background: linear-gradient(to bottom, var(--color-bg-deep), transparent);
  z-index: 4;
  pointer-events: none;
}

.pilares__transition-out {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 200px;
  background: linear-gradient(to bottom, transparent, var(--color-bg-deep));
  z-index: 4;
  pointer-events: none;
}

.pilares__atmosphere {
  position: absolute;
  inset: -30%;
  background: radial-gradient(
      ellipse at 20% 50%,
      rgba(96, 165, 250, 0.05) 0%,
      transparent 50%
    ),
    radial-gradient(
      ellipse at 80% 30%,
      rgba(167, 139, 250, 0.04) 0%,
      transparent 50%
    ),
    radial-gradient(
      ellipse at 50% 80%,
      rgba(52, 211, 153, 0.03) 0%,
      transparent 40%
    );
  z-index: 0;
  animation: atmosphereDrift 25s ease-in-out infinite alternate;
  will-change: transform;
}

.pilares__volumetric {
  position: absolute;
  top: 30%;
  left: 50%;
  width: 500px;
  height: 500px;
  transform: translateX(-50%);
  background: radial-gradient(
    circle,
    rgba(167, 139, 250, 0.06) 0%,
    transparent 70%
  );
  filter: blur(120px);
  mix-blend-mode: color-dodge;
  z-index: 1;
  pointer-events: none;
}

@keyframes atmosphereDrift {
  0% {
    transform: translate(0, 0) rotate(0deg);
  }
  100% {
    transform: translate(-20px, 15px) rotate(2deg);
  }
}

.pilares__inner {
  position: relative;
  z-index: 5;
  max-width: 1200px;
  width: 100%;
}

.pilares__header {
  text-align: center;
  margin-bottom: 5rem;
  visibility: hidden;
}

.pilares__title {
  font-size: clamp(1.8rem, 4vw, 3rem);
  color: var(--color-text-primary);
  margin-bottom: 1rem;
}

.pilares__subtitle {
  font-size: clamp(0.95rem, 1.5vw, 1.15rem);
  color: var(--color-text-secondary);
  max-width: 500px;
  margin: 0 auto;
  line-height: 1.7;
}

.pilares__grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 1.5rem;
  will-change: transform;
  perspective: 800px;
}

.pilar-card {
  position: relative;
  background: var(--color-bg-glass);
  border: 1px solid var(--color-border-glass);
  border-radius: 16px;
  padding: 2.5rem 2rem;
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  overflow: hidden;
  visibility: hidden;
  transform-style: preserve-3d;
  transition: border-color 0.4s var(--ease-out-quart),
    box-shadow 0.4s var(--ease-out-quart);
}

.pilar-card:hover {
  border-color: var(--color-border-glass-strong);
  box-shadow: 0 8px 40px rgba(167, 139, 250, 0.08),
    0 0 80px rgba(167, 139, 250, 0.03);
}

.pilar-card__glow {
  position: absolute;
  inset: 0;
  border-radius: inherit;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.4s var(--ease-out-quart);
  z-index: 0;
}

.pilar-card__shine {
  position: absolute;
  inset: 0;
  border-radius: inherit;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.3s var(--ease-out-quart);
  z-index: 0;
}

.pilar-card__depth {
  position: relative;
  transform: translateZ(30px);
  z-index: 2;
}

.pilar-card::before {
  content: "";
  position: absolute;
  inset: 0;
  background-image: var(--micro-grid);
  background-size: 40px 40px;
  opacity: 0;
  pointer-events: none;
  border-radius: inherit;
  transition: opacity 0.4s var(--ease-out-quart);
  z-index: 0;
}

.pilar-card:hover::before {
  opacity: 1;
}

.pilar-card__texture {
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  border-radius: inherit;
  pointer-events: none;
  opacity: 0;
  mix-blend-mode: luminosity;
  filter: blur(1px);
  transition: opacity 0.8s var(--ease-out-quart),
    filter 0.8s var(--ease-out-quart);
  z-index: 0;
}

.pilar-card:hover .pilar-card__texture {
  opacity: 0.12;
  filter: blur(0px);
}

.pilar-card::after {
  content: "";
  position: absolute;
  inset: 0;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 128 128' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='1.2' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
  background-size: 128px 128px;
  opacity: 0.03;
  pointer-events: none;
  border-radius: inherit;
}

.pilar-card__icon {
  color: var(--color-accent);
  margin-bottom: 1.5rem;
}

.pilar-card__title {
  font-size: 1.15rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
  color: var(--color-text-primary);
}

.pilar-card__desc {
  position: relative;
  font-size: 0.9rem;
  line-height: 1.7;
  color: var(--color-text-secondary);
  z-index: 1;
  transform: translateZ(15px);
}

.pilar-card__index {
  position: absolute;
  top: 1.5rem;
  right: 1.5rem;
  font-family: var(--font-display);
  font-size: 3rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.03);
  line-height: 1;
}

@media (max-width: 768px) {
  .pilares {
    padding: 5rem 1.5rem;
  }

  .pilares__grid {
    grid-template-columns: 1fr;
  }
}
</style>
